// ─────────────────────────────────────────────────────────────────────────────
//  Bazaar Buy Now checkout — Stripe Payment Element for the order deposit.
//  Reached from BazaarListingDetailPage's "Buy Now" button, which creates the
//  order first and hands the resulting clientSecret to this page via router state
//  (Stripe client secrets are single-use-session values, not persisted on the order,
//  so a page refresh can't recover one — see the fallback state below).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, bazaarOrdersService } from '../services/api';
import { BazaarOrderStatus } from '../types';

function CheckoutForm({ orderId, depositAmount }: { orderId: string; depositAmount: number }) {
  const { getToken } = useAppAuth();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message ?? 'Payment failed. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      await bazaarOrdersService(createApiClient(getToken)).confirmPayment(orderId, paymentIntent!.id);
      navigate('/bazaar/mine');
    } catch (err: any) {
      setError(err?.message ?? 'Payment succeeded but we could not confirm your order — contact support.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}
      <button
        data-testid="checkout-pay-button"
        type="submit"
        disabled={!stripe || submitting}
        className="w-full py-3 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {submitting ? 'Processing…' : `Pay £${depositAmount.toFixed(2)} deposit`}
      </button>
    </form>
  );
}

export function BazaarCheckoutPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation() as { state?: { clientSecret?: string; depositAmount?: number } };
  const { getToken, account } = useAppAuth();
  const [clientSecret] = useState<string | undefined>(location.state?.clientSecret);
  const [depositAmount] = useState<number>(location.state?.depositAmount ?? 0);
  // Loaded lazily on mount, not at module scope — this page is statically imported by
  // App.tsx, so a module-scope loadStripe() call fetched js.stripe.com on every page in
  // the app, not just checkout (and tripped third-party-cookie warnings site-wide).
  const [stripePromise] = useState(() =>
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
      ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
      : null
  );

  const { data: order } = useQuery({
    queryKey: ['bazaar', 'order', orderId],
    queryFn: () => bazaarOrdersService(createApiClient(getToken)).getById(orderId!),
    enabled: !!orderId && !!account,
  });

  useEffect(() => {
    document.title = 'Checkout — TransitDeal';
  }, []);

  if (!account) return null;

  return (
    <div data-testid="bazaar-checkout-screen" className="page-shell-tight">
      <Link to="/bazaar/mine" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to My Bazaar
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Checkout</h1>

      {order && order.status !== BazaarOrderStatus.PendingDeposit ? (
        <div className="space-y-4">
          <div data-testid="checkout-already-paid" className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4 text-sm font-semibold">
            <CheckCircle className="w-4 h-4" /> This order's deposit has already been paid.
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-800 mb-1">Getting your items delivered?</p>
            <p className="text-xs text-gray-500 mb-3">
              Post a delivery job for any item and TransitDeal carriers will quote to collect and deliver it.
            </p>
            <div className="divide-y divide-gray-100">
              {order.items.map(item => (
                <div key={item.id} className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-700 truncate mr-2">{item.listingTitle}</span>
                  {item.linkedDeliveryJobId ? (
                    <span className="flex items-center gap-1 text-xs font-semibold text-green-700">
                      <CheckCircle className="w-3.5 h-3.5" /> Job posted
                    </span>
                  ) : (
                    <Link
                      data-testid={`post-delivery-job-${item.id}`}
                      to={`/post-job?${new URLSearchParams({
                        description: `Delivery of: ${item.listingTitle}`,
                        bazaarOrderId: order.id,
                        bazaarOrderItemId: item.id,
                      })}`}
                      className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1.5 hover:bg-green-100"
                    >
                      Post a delivery job
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : clientSecret && stripePromise ? (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm orderId={orderId!} depositAmount={depositAmount} />
          </Elements>
        </div>
      ) : (
        <div data-testid="checkout-session-expired" className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-xl p-4">
          This checkout session is no longer available in this tab. Please return to the listing and tap
          "Buy Now" again to start a fresh payment.
        </div>
      )}
    </div>
  );
}
